import React, { useState, useEffect } from 'react';
import { FirebaseUser } from '../types/user';
import { db } from '../services/firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';

interface SpecialityAdditionModalProps {
  user: FirebaseUser;
  onClose: () => void;
  onUpdate: () => void; // Callback to refresh parent data
}

interface NewSpecialtyData {
  specialtyId: string;
  subscription: string;
  allowance: number;
  expiry: Date;
}

const SpecialityAdditionModal: React.FC<SpecialityAdditionModalProps> = ({ 
  user, 
  onClose, 
  onUpdate 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewSpecialtyData>({
    specialtyId: '',
    subscription: '',
    allowance: 0,
    expiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // Default to 30 days from now
  });

  // Disable background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.specialtyId.trim()) {
      setError('Specialty ID is required');
      return;
    }

    // Check if specialty already exists
    if (user.unlockedSpecialties && user.unlockedSpecialties.includes(formData.specialtyId)) {
      setError('This specialty already exists for this user');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Prepare update data
      const updateData: any = {};
      
      // Add to unlockedSpecialties array
      updateData.unlockedSpecialties = arrayUnion(formData.specialtyId);
      
      // Add subscription
      updateData[`specialtySubscriptions.${formData.specialtyId}`] = formData.subscription;
      
      // Add allowance
      updateData[`interviewAllowance.${formData.specialtyId}`] = formData.allowance;
      
      // Add expiry - store as ISO string
      updateData[`unlockedSpecialtyExpiries.${formData.specialtyId}`] = formData.expiry.toISOString();

      await updateDoc(userRef, updateData);
      
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error adding specialty:', err);
      setError('Failed to add specialty. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: '#2d2d2d',
          border: '1px solid #404040',
          borderRadius: '8px',
          width: '90vw',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          backgroundColor: '#404040',
          borderBottom: '1px solid #555',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{margin: 0, color: '#e0e0e0', fontSize: '20px'}}>
            Add New Specialty
          </h3>
          <button
            onClick={onClose}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          backgroundColor: '#2d2d2d',
          overflow: 'auto'
        }}>
          {error && (
            <div style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            {/* Specialty ID */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Specialty ID <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type="text"
                value={formData.specialtyId}
                onChange={(e) => setFormData(prev => ({...prev, specialtyId: e.target.value}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
                placeholder="e.g., cardiology, neurology, pediatrics"
              />
              <small style={{color: '#888', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                Use lowercase with hyphens for multi-word specialties (e.g., emergency-medicine)
              </small>
            </div>

            {/* Subscription Plan */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Subscription Plan
              </label>
              <input
                type="text"
                value={formData.subscription}
                onChange={(e) => setFormData(prev => ({...prev, subscription: e.target.value}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
                placeholder="Enter subscription plan"
              />
            </div>

            {/* Interview Allowance */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Interview Allowance
              </label>
              <input
                type="number"
                value={formData.allowance}
                onChange={(e) => setFormData(prev => ({...prev, allowance: Number(e.target.value)}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
                min="0"
                placeholder="Enter interview allowance"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Expiry Date & Time
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.expiry)}
                onChange={(e) => setFormData(prev => ({...prev, expiry: new Date(e.target.value)}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          backgroundColor: '#333',
          borderTop: '1px solid #555',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !formData.specialtyId.trim()}
            style={{
              background: formData.specialtyId.trim() ? '#28a745' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isLoading || !formData.specialtyId.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading || !formData.specialtyId.trim() ? 0.6 : 1
            }}
          >
            {isLoading ? 'Adding...' : 'Add Specialty'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpecialityAdditionModal;